import { useEffect, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';

import LucideSymbolIcon from '../../components/icons/LucideSymbolIcon';
import AcademiaAuthForm from '../../components/auth/AcademiaAuthForm';
import AuthMarketingAsideBackground from '../../components/auth/AuthMarketingAsideBackground';
import { usePublicBranding } from '../../hooks/usePublicBranding';
import { fetchFilieres } from '../../services/academiaApprenantService';
import { getDomaineMetaByKey } from '../../constants/academiaFiliereDomains';
import { resolveBrandingLogoUrl } from '../../utils/brandingUrl';
import {
  findPublicFiliereByCode,
  findPublicFiliereByCoursSlug,
  type PublicFiliere,
} from '../../utils/publicFilieres';
import { JCONSULT_LOGO, JCONSULT_LOGO_WHITE } from '../../config/brandingAssets';
import { setPendingFiliereSubscription } from '../../utils/pendingFiliereSubscription';

type AuthMode = 'login' | 'register';

interface LoginPageProps {
  onSuccess: (nextState: unknown) => void;
}

export default function LoginPage({ onSuccess }: LoginPageProps) {
  const [searchParams] = useSearchParams();
  const filiereCode = searchParams.get('filiere');
  const coursSlug = searchParams.get('cours');
  const [selectedFiliere, setSelectedFiliere] = useState<PublicFiliere | null>(null);
  const referralCode = (
    searchParams.get('codeParrain') ||
    searchParams.get('parrain') ||
    searchParams.get('code') ||
    ''
  )
    .trim()
    .toUpperCase();
  const initialMode: AuthMode =
    searchParams.get('mode') === 'register' || Boolean(referralCode) ? 'register' : 'login';
  const [mode, setMode] = useState<AuthMode>(initialMode);
  const branding = usePublicBranding();

  useEffect(() => {
    const code = (
      searchParams.get('codeParrain') ||
      searchParams.get('parrain') ||
      searchParams.get('code') ||
      ''
    )
      .trim()
      .toUpperCase();
    const nextMode: AuthMode =
      searchParams.get('mode') === 'register' || Boolean(code) ? 'register' : 'login';
    setMode(nextMode);
  }, [searchParams]);

  useEffect(() => {
    if (!filiereCode && !coursSlug) {
      setSelectedFiliere(null);
      return;
    }
    let cancelled = false;
    void (async () => {
      try {
        const rows = await fetchFilieres();
        if (cancelled) return;
        const found =
          findPublicFiliereByCode(rows, filiereCode) ??
          findPublicFiliereByCoursSlug(rows, coursSlug);
        setSelectedFiliere(found ?? null);
      } catch {
        if (!cancelled) setSelectedFiliere(null);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [filiereCode, coursSlug]);

  useEffect(() => {
    if (selectedFiliere?.id) {
      setPendingFiliereSubscription(selectedFiliere.id);
    }
  }, [selectedFiliere?.id]);

  const supportEmail = branding.supportEmail;
  const customLogoSrc = resolveBrandingLogoUrl(branding.logoUrl);
  const logoOnDark = customLogoSrc || JCONSULT_LOGO_WHITE;
  const logoOnLight = customLogoSrc || JCONSULT_LOGO;
  const domainLabel = selectedFiliere
    ? getDomaineMetaByKey(selectedFiliere.domaine)?.title ?? selectedFiliere.domaine
    : null;

  return (
    <main className="flex h-screen w-full overflow-hidden bg-surface text-on-surface antialiased">
      <section className="relative hidden min-w-0 lg:flex lg:h-screen lg:w-1/2">
        <AuthMarketingAsideBackground />

        <div className="relative z-10 flex h-full w-full flex-col justify-between p-8 text-white xl:p-10">
          <div className="w-full max-w-lg">
            <div className="mb-4 flex w-full justify-center">
              <img
                src={logoOnDark}
                alt="Logo JCONSULT"
                className="h-auto w-[18rem] max-w-full object-contain"
              />
            </div>
            <h2 className="mb-2 text-3xl font-extrabold leading-tight tracking-tight xl:text-4xl">
              Votre avenir professionnel commence ici
            </h2>

            <div className="mt-8 grid grid-cols-2 gap-3 sm:mt-10">
              <div className="glass-card rounded-lg border border-white/10 p-3">
                <span className="block text-xl font-bold">19+</span>
                <span className="text-[10px] uppercase tracking-widest text-on-primary-container">Filières certifiantes</span>
              </div>
              <div className="glass-card rounded-lg border border-white/10 p-3">
                <span className="block text-xl font-bold">CDF</span>
                <span className="text-[10px] uppercase tracking-widest text-on-primary-container">Paiement en ligne</span>
              </div>
            </div>
          </div>

          <div className="flex w-full shrink-0 items-center justify-between gap-4 pt-4">
            <p className="text-[11px] text-on-primary-container/80">
              Besoin d&apos;aide ?{' '}
              <a href={`mailto:${supportEmail}`} className="font-semibold text-white hover:underline">
                Support
              </a>
              {' · '}
              <Link to="/legal/cgu" className="font-semibold text-white hover:underline">
                CGU
              </Link>
              {' · '}
              <Link to="/legal/confidentialite" className="font-semibold text-white hover:underline">
                Confidentialité
              </Link>
            </p>
            <div className="flex shrink-0 gap-3">
              <LucideSymbolIcon name="public" tone="inherit" className="text-on-primary-container/70" size={18} />
              <LucideSymbolIcon name="security" tone="inherit" className="text-on-primary-container/70" size={18} />
            </div>
          </div>
        </div>
      </section>

      <section className="flex h-screen min-h-0 min-w-0 w-full flex-1 flex-col bg-surface-container-lowest lg:w-1/2">
        <div className="mx-auto w-full max-w-xl shrink-0 px-4 pt-5 sm:px-6 sm:pt-6">
          <Link
            to="/"
            className="inline-flex items-center gap-1 text-xs font-semibold text-primary hover:underline sm:text-sm"
          >
            ← Retour au site
          </Link>
        </div>
        <div className="mx-auto flex min-h-0 w-full max-w-xl flex-1 flex-col justify-center overflow-y-auto px-4 pb-3 sm:px-6 sm:pb-4">
          {mode === 'login' ? (
            <div className="mb-3 flex w-full shrink-0 items-center justify-center lg:hidden">
              <img
                src={logoOnLight}
                alt="Logo JCONSULT"
                className="mx-auto h-auto w-28 object-contain sm:w-32"
              />
            </div>
          ) : null}
          <div className="mb-5 shrink-0 text-left sm:mb-6">
            <h1
              className={`text-xl font-black tracking-tight text-on-surface sm:text-2xl ${
                mode === 'login' ? '-mt-1.5 sm:-mt-2' : '-mt-2 sm:-mt-2.5'
              }`}
            >
              {mode === 'login' ? 'Bienvenue' : 'Créer un compte'}
            </h1>
          </div>

          {selectedFiliere ? (
            <div className="mb-3 shrink-0 rounded-xl border border-primary/25 bg-primary/[0.07] px-3 py-2.5 text-sm shadow-sm">
              <p className="text-[10px] font-bold uppercase tracking-wider text-primary">Formation sélectionnée</p>
              <p className="mt-0.5 text-sm font-bold text-slate-900">{selectedFiliere.libelle}</p>
              {domainLabel ? (
                <p className="text-[11px] text-on-surface-variant">{domainLabel} · {selectedFiliere.code}</p>
              ) : (
                <p className="text-[11px] text-on-surface-variant">Code : {selectedFiliere.code}</p>
              )}
            </div>
          ) : null}

          {referralCode && mode === 'register' ? (
            <div className="mb-2 shrink-0 rounded-xl border border-primary/30 bg-primary/[0.08] px-3 py-2 text-xs">
              <span className="font-bold text-primary">Parrainage · </span>
              <span className="text-on-surface-variant">
                code <span className="font-bold text-slate-900">{referralCode}</span>
              </span>
            </div>
          ) : null}

          <AcademiaAuthForm
            mode={mode}
            onModeChange={setMode}
            onSuccess={onSuccess}
            initialCodeParrain={referralCode}
          />
        </div>
        <p className="mx-auto w-full max-w-xl shrink-0 px-4 pb-4 text-center text-[11px] text-on-surface-variant sm:px-6 sm:text-xs lg:hidden">
          Besoin d&apos;aide ?{' '}
          <a href={`mailto:${supportEmail}`} className="font-semibold text-primary">
            Support
          </a>
          {' · '}
          <Link to="/legal/cgu" className="font-semibold text-primary hover:underline">
            CGU
          </Link>
          {' · '}
          <Link to="/legal/confidentialite" className="font-semibold text-primary hover:underline">
            Confidentialité
          </Link>
        </p>
      </section>
    </main>
  );
}
